/**
 * useTheme — Phase 3.9 — three FE themes (Default / Obsidian / Midnight Blue).
 *
 * Source of truth is the DOM (`<html data-theme>`); the inline pre-paint
 * script in `index.html` sets it before React mounts. This hook is a thin
 * wrapper around DOM read/write + localStorage cache + the BE PATCH, with
 * a `useMe`-driven reconcile so the BE wins for cross-device sync.
 *
 * No React Context, no Provider — theme changes are CSS-var swaps that
 * cascade through every utility. Only the picker re-renders on change.
 */

import { THEMES, type Theme } from '@/api/types-bridge'
import { useMe } from '@/features/me/use-me'
import { useUpdateMyProfile } from '@/features/tenant-settings/use-my-profile'
import { useCallback, useEffect, useRef, useState } from 'react'

const LS_KEY = 'theme'
const DEFAULT_THEME: Theme = 'default'

const isTheme = (v: unknown): v is Theme =>
  typeof v === 'string' && (THEMES as readonly string[]).includes(v)

const readDom = (): Theme => {
  if (typeof document === 'undefined') return DEFAULT_THEME
  const attr = document.documentElement.getAttribute('data-theme')
  return isTheme(attr) ? attr : DEFAULT_THEME
}

const applyTheme = (next: Theme) => {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', next)
  }
  try {
    localStorage.setItem(LS_KEY, next)
  } catch {
    // Private-mode Safari etc. — non-fatal; the DOM attribute is the
    // load-bearing surface, the localStorage cache is a fast path.
  }
}

export interface UseThemeReturn {
  /** The currently-applied theme slug. */
  theme: Theme
  /** All available themes, in display order. */
  themes: typeof THEMES
  /**
   * Switch to `next`. Updates DOM + localStorage instantly (CSS cascade
   * flips the theme), then PATCHes `/api/me/profile { theme }` to
   * persist. On PATCH failure, reverts DOM + localStorage and rethrows
   * so the caller can toast.
   */
  setTheme: (next: Theme) => Promise<void>
  /** Whether the BE PATCH is in flight. */
  isSaving: boolean
}

export function useTheme(): UseThemeReturn {
  const me = useMe()
  const update = useUpdateMyProfile()
  const [theme, setThemeState] = useState<Theme>(readDom)

  /**
   * Mirror of the applied theme that is readable SYNCHRONOUSLY. The
   * MutationObserver below fires as a microtask, which can land before
   * React has committed a `setThemeState` this hook itself just made —
   * so comparing against `theme` would report a spurious change and
   * enqueue a redundant update. Every write below advances the ref in
   * the same statement as the state write.
   */
  const appliedRef = useRef<Theme>(theme)

  const adopt = useCallback((next: Theme) => {
    if (appliedRef.current === next) return
    appliedRef.current = next
    setThemeState(next)
  }, [])

  // Reconcile from BE on first /api/me load. BE wins for cross-device
  // consistency. Effect deps include only the remote value so a local
  // setTheme doesn't re-trigger this loop.
  const remote = me.data?.user.theme
  useEffect(() => {
    if (remote && isTheme(remote) && remote !== readDom()) {
      applyTheme(remote)
      adopt(remote)
    }
  }, [remote, adopt])

  // Follow the DOM. Some consumers read the theme from React state rather
  // than from the CSS cascade — the theme picker's selected-card ring and
  // the emoji picker's palette — so a `data-theme` flip made by anything
  // other than `setTheme` (the pre-paint script racing hydration, a
  // devtools edit, an automated capture job shooting a light/dark pair
  // off one page state) would leave them rendering the previous theme. This mirrors DOM →
  // state only; `applyTheme` stays the sole writer of the attribute, so
  // it cannot loop.
  useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return
    const sync = () => adopt(readDom())
    const observer = new MutationObserver(sync)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    })
    // The attribute can move between the `useState` initialiser and this
    // effect — the reconcile effect above runs first on mount.
    sync()
    return () => observer.disconnect()
  }, [adopt])

  const setTheme = useCallback(
    async (next: Theme) => {
      const previous = readDom()
      applyTheme(next)
      adopt(next)
      try {
        await update.mutateAsync({ theme: next })
      } catch (err) {
        applyTheme(previous)
        adopt(previous)
        throw err
      }
    },
    [update, adopt],
  )

  return { theme, themes: THEMES, setTheme, isSaving: update.isPending }
}
